import { useMemo, useState, type FC } from 'react';

import { Box, Button, Stack, Typography } from '@mui/material';
import { useIsFetching } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import { useConnection } from 'wagmi';

import AllCampaigns from '@/components/AllCampaigns';
import AutojoinInfoWidget from '@/components/AutojoinInfoWidget';
import CampaignsFilters, {
  type CampaignsFiltersSelection,
} from '@/components/CampaignsFilters';
import CampaignsTabs from '@/components/CampaignsTabs';
import CampaignsViewToggle from '@/components/CampaignsViewToggle';
import HostedCampaigns from '@/components/HostedCampaigns';
import JoinedCampaigns from '@/components/JoinedCampaigns';
import LaunchCampaignButton from '@/components/LaunchCampaignButton';
import { useReserveLayoutBottomOffset } from '@/components/Layout';
import MobileBottomNav from '@/components/MobileBottomNav';
import PageWrapper from '@/components/PageWrapper';
import { ROUTES, PERSISTED_CAMPAIGNS_VIEW_KEY } from '@/constants';
import { AUTHED_QUERY_TAG, QUERY_KEYS } from '@/constants/queryKeys';
import { useIsMobile } from '@/hooks/useBreakpoints';
import usePagination from '@/hooks/usePagination';
import { ApiKeyIcon } from '@/icons';
import { useActiveAccount } from '@/providers/ActiveAccountProvider';
import { useNetwork } from '@/providers/NetworkProvider';
import { useWeb3Auth } from '@/providers/Web3AuthProvider';
import {
  type CampaignsQueryParams,
  CampaignStatus,
  CampaignsTabFilter as TabFilter,
} from '@/types';
import { filterFalsyQueryParams } from '@/utils';

const FILTER_STATUS_TO_CAMPAIGN_STATUSES: Record<
  CampaignStatus,
  CampaignStatus[]
> = {
  [CampaignStatus.ACTIVE]: [CampaignStatus.ACTIVE, CampaignStatus.TO_CANCEL],
  [CampaignStatus.TO_CANCEL]: [CampaignStatus.TO_CANCEL],
  [CampaignStatus.CANCELLED]: [CampaignStatus.CANCELLED],
  [CampaignStatus.COMPLETED]: [CampaignStatus.COMPLETED],
};

const Campaigns: FC = () => {
  const { appChainId } = useNetwork();
  const [view, setView] = useState<'grid' | 'table'>(() => {
    const persistedView = localStorage.getItem(PERSISTED_CAMPAIGNS_VIEW_KEY);
    return persistedView === 'table' ? 'table' : 'grid';
  });
  const [tabFilter, setTabFilter] = useState<TabFilter>(TabFilter.ALL);
  const [appliedFilters, setAppliedFilters] =
    useState<CampaignsFiltersSelection>({
      network: appChainId,
      statuses: [CampaignStatus.ACTIVE],
      campaignTypes: [],
      exchanges: [],
    });

  const navigate = useNavigate();
  const { isConnected } = useConnection();
  const { isAuthenticated } = useWeb3Auth();
  const { activeAddress } = useActiveAccount();
  const {
    params: { limit, skip },
    resetPage,
    setNextPage,
  } = usePagination();

  const isMobile = useIsMobile();
  useReserveLayoutBottomOffset(isMobile);

  const isGridView = view === 'grid';

  const launcherFilter = useMemo(() => {
    if (tabFilter === TabFilter.HOSTED) {
      return activeAddress;
    }
    return undefined;
  }, [tabFilter, activeAddress]);

  const selectedCampaignTypes = appliedFilters.campaignTypes.filter(
    (campaignType) => campaignType !== 'all'
  );
  const selectedExchanges = appliedFilters.exchanges.filter(
    (exchange) => exchange !== 'all'
  );

  const filteredStatuses = appliedFilters.statuses.filter(
    (status) => status !== 'all'
  );
  const selectedStatuses = [
    ...new Set(
      filteredStatuses.flatMap(
        (status) => FILTER_STATUS_TO_CAMPAIGN_STATUSES[status]
      )
    ),
  ];

  const queryParams = filterFalsyQueryParams({
    chain_id: appliedFilters.network,
    status: selectedStatuses,
    launcher: launcherFilter,
    type: selectedCampaignTypes,
    exchange: selectedExchanges,
    limit,
    skip,
  }) as CampaignsQueryParams;

  const filtersCount = useMemo(() => {
    let count = 0;
    Object.entries(appliedFilters)
      .filter(([key]) => key !== 'network')
      .forEach(([_, value]) => {
        if (Array.isArray(value)) {
          count += Number(value.length > 0);
        } else {
          count += Number(!!value);
        }
      });
    return count;
  }, [appliedFilters]);

  const isJoinedTab = tabFilter === TabFilter.JOINED;
  const isHostedTab = tabFilter === TabFilter.HOSTED;
  const isAllTab = !isJoinedTab && !isHostedTab;

  const commonKeys = [
    appliedFilters.network,
    selectedStatuses,
    selectedCampaignTypes,
    selectedExchanges,
    limit,
    skip,
  ];
  const activeKey = isJoinedTab
    ? [
        QUERY_KEYS.JOINED_CAMPAIGNS,
        isAuthenticated,
        ...commonKeys,
        AUTHED_QUERY_TAG,
      ]
    : isHostedTab
      ? [QUERY_KEYS.HOSTED_CAMPAIGNS, launcherFilter, ...commonKeys]
      : [QUERY_KEYS.ALL_CAMPAIGNS, ...commonKeys];

  const isFetchingCount = useIsFetching({ queryKey: activeKey });

  const disableFilters = isFetchingCount > 0;

  const handleSetTabFilter = (nextTab: TabFilter) => {
    resetPage();
    setTabFilter(nextTab);
  };

  const handleApplyFilters = (nextFilters: CampaignsFiltersSelection) => {
    resetPage();
    setAppliedFilters(nextFilters);
  };

  const handleChangeView = (nextView: 'grid' | 'table') => {
    setView(nextView);
    localStorage.setItem(PERSISTED_CAMPAIGNS_VIEW_KEY, nextView);
  };

  return (
    <PageWrapper>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'flex-start', md: 'center' },
          justifyContent: 'space-between',
          mb: { xs: 4, md: 6 },
          mx: { xs: -2, md: 0 },
          pb: { xs: 3, md: 0 },
          px: { xs: 2, md: 0 },
          gap: { xs: 3, md: 4 },
          borderBottom: { xs: '1px solid', md: 'none' },
          borderColor: { xs: 'border.strong', md: 'unset' },
        }}
      >
        <Stack>
          <Typography
            variant={isMobile ? 'h5' : 'h3'}
            sx={{
              color: 'neutral.100',
              fontWeight: isMobile ? 600 : 800,
              mb: { xs: 0.5, md: 1.5 },
              letterSpacing: isMobile ? '0px' : '-1.5px',
            }}
          >
            Campaigns
          </Typography>
          <Typography variant="body3" sx={{ color: 'text.muted' }}>
            Join or host trading campaigns to earn rewards.
          </Typography>
        </Stack>
        <Box
          sx={{
            display: 'flex',
            gap: 2,
          }}
        >
          {isAuthenticated && (
            <Button
              variant="outlined"
              size={isMobile ? 'medium' : 'large'}
              color="accent"
              sx={{ color: 'white' }}
              onClick={() => navigate(ROUTES.MANAGE_API_KEYS)}
              startIcon={<ApiKeyIcon />}
            >
              Manage API Keys
            </Button>
          )}
          {isConnected && (
            <LaunchCampaignButton size={isMobile ? 'medium' : 'large'} />
          )}
        </Box>
      </Box>
      <AutojoinInfoWidget />
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          mb: 4,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            position: 'relative',
            minWidth: 0,
            gap: 1.5,
          }}
        >
          <CampaignsTabs
            activeTab={tabFilter}
            setActiveTab={handleSetTabFilter}
            isDisabled={disableFilters}
          />
          <CampaignsFilters
            appliedFilters={appliedFilters}
            filtersCount={filtersCount}
            handleApplyFilters={handleApplyFilters}
            isDisabled={disableFilters}
          />
        </Box>
        <CampaignsViewToggle
          isGridView={isGridView}
          disableFilters={disableFilters}
          onViewChange={handleChangeView}
        />
      </Box>
      {isJoinedTab && (
        <JoinedCampaigns
          queryParams={queryParams}
          isGridView={isGridView}
          setNextPage={setNextPage}
          hasActiveFilters={filtersCount > 0}
        />
      )}
      {isHostedTab && (
        <HostedCampaigns
          queryParams={queryParams}
          isGridView={isGridView}
          setNextPage={setNextPage}
          hasActiveFilters={filtersCount > 0}
        />
      )}
      {isAllTab && (
        <AllCampaigns
          queryParams={queryParams}
          isGridView={isGridView}
          setNextPage={setNextPage}
          hasActiveFilters={filtersCount > 0}
        />
      )}
      <MobileBottomNav isVisible={isMobile} />
    </PageWrapper>
  );
};

export default Campaigns;
